import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, AlertCircle, X, PackagePlus, Check, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry'
];

const emptyLineItem = () => ({
  key: Date.now() + Math.random(),
  product_id: '',
  serial_number: '',
  description: '',
  mrp: '',
  quantity: 1,
  unit_id: '',
  price_per_unit: '',
  discount_percentage: 0,
  discount_amount: 0,
  tax_id: '',
  tax_amount: 0,
  total_amount: 0,
  warranty_days: '',
  part_sale_amount: '',
  _is_part_sale: false,
  _is_full_sale: false,
  _part_sale_unit_id: '',
  _stock_count: null,
  _sold_item_ids: [],
  _items_confirmed: false,
});

export const SaleInvoiceCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromDcId = searchParams.get('from_dc');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sourceDcId, setSourceDcId] = useState(fromDcId || null);

  // Master data
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [units, setUnits] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);

  // Header
  const [saleType, setSaleType] = useState('Cash');
  const [customerId, setCustomerId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [stateOfSupply, setStateOfSupply] = useState('');
  const [lutArnNumber, setLutArnNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [paymentTermId, setPaymentTermId] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState([emptyLineItem()]);

  // Item selection modal
  const [itemModalIdx, setItemModalIdx] = useState(null);
  const [itemModalIds, setItemModalIds] = useState([]);
  const [itemModalAvailable, setItemModalAvailable] = useState([]);
  const [itemModalErrors, setItemModalErrors] = useState([]);
  const [itemModalValidating, setItemModalValidating] = useState(false);

  // Payment
  const [payments, setPayments] = useState([{ key: 1, payment_type: 'Cash', amount: 0 }]);
  const [description, setDescription] = useState('');
  const [roundOff, setRoundOff] = useState(true);
  const [isReceived, setIsReceived] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [customersRes, companiesRes, productsRes, taxesRes, unitsRes, ptRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders())
        ]);
        setCustomers(customersRes.data.filter(c => c.active));
        setCompanies(companiesRes.data.filter(c => c.active));
        setProducts(productsRes.data);
        setTaxes(taxesRes.data.filter(t => t.active));
        setUnits(unitsRes.data.filter(u => u.active));
        setPaymentTerms(ptRes.data.filter(p => p.active));
        const pmRes = await axios.get(`${BACKEND_URL}/api/payment-modes`, getAuthHeaders());
        setPaymentModes(pmRes.data.filter(p => p.active));
      } catch (err) {
        setError('Failed to load master data');
      }
    };
    fetchMasterData();
  }, []);

  // Load DC prefill data from sessionStorage
  useEffect(() => {
    if (!fromDcId) return;
    try {
      const raw = sessionStorage.getItem('dc_sale_prefill');
      if (!raw) return;
      const prefill = JSON.parse(raw);
      sessionStorage.removeItem('dc_sale_prefill');
      setSourceDcId(prefill.source_dc_id || fromDcId);
      if (prefill.customer_id) setCustomerId(prefill.customer_id);
      if (prefill.company_id) setCompanyId(prefill.company_id);
      if (prefill.state_of_supply) setStateOfSupply(prefill.state_of_supply);
      if (prefill.description) setDescription(prefill.description);
      if (prefill.line_items?.length) {
        const items = prefill.line_items.map(li => ({
          ...emptyLineItem(),
          product_id: li.product_id || '',
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: li.quantity || 1,
          unit_id: li.unit_id || '',
          price_per_unit: li.price_per_unit || '',
          discount_percentage: li.discount_percentage || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          _sold_item_ids: li.sold_item_ids || [],
          _items_confirmed: (li.sold_item_ids?.length || 0) > 0,
          _stock_count: li.sold_item_ids?.length || 0,
        }));
        items.push(emptyLineItem());
        setLineItems(items);
      }
    } catch { /* ignore parse errors */ }
  }, [fromDcId]);


  // Auto-fill billing address when customer changes
  useEffect(() => {
    if (customerId) {
      const cust = customers.find(c => c.id === customerId);
      if (cust && cust.billing_address) {
        setBillingAddress(cust.billing_address);
      }
    }
  }, [customerId, customers]);

  const recalcLine = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price_per_unit) || 0;
    const discPct = parseFloat(item.discount_percentage) || 0;
    const lineTotal = qty * price;
    const discAmt = lineTotal * discPct / 100;
    const taxableAmount = lineTotal - discAmt;
    const tax = taxes.find(t => t.id === item.tax_id);
    const taxPct = tax ? parseFloat(tax.percentage) : 0;
    const taxAmt = taxableAmount * taxPct / 100;
    const totalAmt = taxableAmount + taxAmt;
    return {
      ...item,
      discount_amount: Math.round(discAmt * 100) / 100,
      tax_amount: Math.round(taxAmt * 100) / 100,
      total_amount: Math.round(totalAmt * 100) / 100
    };
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => p.id === value);
        if (prod) {
          if (prod.is_part_sale) {
            // Default to part sale mode: use part_sale_unit_id
            updated[index].unit_id = prod.part_sale_unit_id || prod.unit_id;
            updated[index]._part_sale_unit_id = prod.part_sale_unit_id || '';
            updated[index]._is_full_sale = false;
          } else {
            updated[index].unit_id = prod.unit_id || '';
            updated[index]._part_sale_unit_id = '';
            updated[index]._is_full_sale = false;
          }
        }
        updated[index]._is_part_sale = prod?.is_part_sale || false;
        updated[index].part_sale_amount = '';
        updated[index]._sold_item_ids = [];
        updated[index]._items_confirmed = false;
        updated[index]._stock_count = null;
        // Fetch stock count
        if (value) {
          axios.get(`${BACKEND_URL}/api/inventory/${value}/available-items`, getAuthHeaders())
            .then(res => {
              setLineItems(p => {
                const u = [...p];
                const li = u.find(i => i.key === updated[index].key);
                if (li) li._stock_count = res.data.stock_count;
                return u;
              });
            }).catch(() => {});
        }
        if (value && !updated[updated.length - 1]?.product_id) return updated.map(i => recalcLine(i));
        if (value) updated.push(emptyLineItem());
      }
      if (field === 'quantity') {
        updated[index]._sold_item_ids = [];
        updated[index]._items_confirmed = false;
      }
      if (field === '_is_full_sale') {
        const prod = products.find(p => p.id === updated[index].product_id);
        if (value) {
          // Full sale: use product unit, clear part sale amount
          updated[index].unit_id = prod?.unit_id || '';
          updated[index].part_sale_amount = '';
        } else {
          // Part sale: use part sale unit
          updated[index].unit_id = prod?.part_sale_unit_id || prod?.unit_id || '';
        }
      }
      updated[index] = recalcLine(updated[index]);
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Item selection modal
  const openItemModal = async (idx) => {
    const li = lineItems[idx];
    if (!li.product_id) return;
    const qty = parseInt(li.quantity) || 1;
    setItemModalIdx(idx);
    setItemModalIds(li._sold_item_ids.length === qty ? [...li._sold_item_ids] : Array(qty).fill(''));
    setItemModalErrors([]);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items`, getAuthHeaders());
      setItemModalAvailable(res.data.items || []);
    } catch { setItemModalAvailable([]); }
  };

  const validateAndConfirmItems = async () => {
    const li = lineItems[itemModalIdx];
    const ids = itemModalIds.map(s => s.trim()).filter(Boolean);
    if (ids.length !== itemModalIds.length) {
      setItemModalErrors(['All item ID fields must be filled']);
      return;
    }
    // Check duplicates
    const dupes = ids.filter((v, i, a) => a.indexOf(v) !== i);
    if (dupes.length) {
      setItemModalErrors([`Duplicate item IDs: ${[...new Set(dupes)].join(', ')}`]);
      return;
    }
    setItemModalValidating(true);
    setItemModalErrors([]);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/inventory/validate-items`, {
        product_id: li.product_id, item_ids: ids
      }, getAuthHeaders());
      if (res.data.valid) {
        setLineItems(prev => {
          const u = [...prev];
          u[itemModalIdx] = { ...u[itemModalIdx], _sold_item_ids: ids, _items_confirmed: true };
          return u;
        });
        setItemModalIdx(null);
      } else {
        setItemModalErrors(res.data.errors || ['Validation failed']);
      }
    } catch { setItemModalErrors(['Failed to validate items']); }
    finally { setItemModalValidating(false); }
  };

  // Check if all product line items have confirmed items
  const allItemsConfirmed = lineItems.filter(li => li.product_id).every(li => {
    const qty = parseInt(li.quantity) || 0;
    if (qty <= 0) return true;
    if (li._stock_count === 0 || li._stock_count === null) return true; // no inventory tracked
    return li._items_confirmed && li._sold_item_ids.length === qty;
  });

  const addPayment = () => setPayments(prev => [...prev, { key: Date.now(), payment_type: 'Cash', amount: 0 }]);

  const updatePayment = (index, field, value) => {
    setPayments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removePayment = (index) => {
    if (payments.length <= 1) return;
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  // Calculations
  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  const roundOffAmount = roundOff ? Math.round(subtotal) - subtotal : 0;
  const total = roundOff ? Math.round(subtotal) : Math.round(subtotal * 100) / 100;
  const received = isReceived ? (parseFloat(receivedAmount) || total) : 0;
  const balance = Math.round((total - received) * 100) / 100;

  const totalQty = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!customerId) { setError('Please select a customer'); return; }
    if (!lineItems.some(li => li.product_id)) { setError('Please add at least one product'); return; }
    if (!allItemsConfirmed) { setError('Please select item IDs for all products with inventory'); return; }

    setSubmitting(true);
    try {
      const payload = {
        sale_type: saleType,
        customer_id: customerId,
        company_id: companyId || null,
        payment_term_id: paymentTermId || null,
        lut_arn_number: lutArnNumber || null,
        po_number: poNumber || null,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        state_of_supply: stateOfSupply,
        billing_address: billingAddress || null,
        source_dc_id: sourceDcId || null,
        line_items: lineItems.filter(li => li.product_id).map(li => ({
          product_id: li.product_id,
          description: li.description,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id || null,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount,
          warranty_days: li.warranty_days ? parseInt(li.warranty_days) : null,
          part_sale_amount: (!li._is_full_sale && li.part_sale_amount) ? parseFloat(li.part_sale_amount) : null,
          sold_item_ids: li._sold_item_ids.length > 0 ? li._sold_item_ids : null
        })),
        payments: payments.map(p => ({
          payment_type: p.payment_type,
          amount: parseFloat(p.amount) || 0,
          bank_detail_id: p.bank_detail_id || null
        })),
        description: description || null,
        round_off: roundOff,
        round_off_amount: Math.round(roundOffAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        total,
        received_amount: received,
        balance
      };

      await axios.post(`${BACKEND_URL}/api/sale-invoices`, payload, getAuthHeaders());
      navigate('/admin/sale/invoices');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save sale invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sale-invoice-create-page" className="pb-24">
      <button onClick={() => navigate('/admin/sale/invoices')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Sale Invoices
      </button>

      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">
        Create Sale Invoice
        {sourceDcId && <span className="text-base font-medium text-amber-600 ml-3">(from Delivery Challan)</span>}
      </h1>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ========== HEADER SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Sale Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sale Type</label>
            <div className="flex gap-4">
              {['Cash', 'Credit'].map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="saleType" value={type} checked={saleType === type}
                    onChange={(e) => setSaleType(e.target.value)} data-testid={`sale-type-${type.toLowerCase()}`}
                    className="w-4 h-4 text-orange-500 border-slate-300" />
                  <span className="text-sm text-slate-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Customer <span className="text-red-500">*</span></label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="select-customer"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="select-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          {/* Invoice Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g., INV/25-26/001"
              data-testid="input-invoice-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Invoice Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
              data-testid="input-invoice-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* State of Supply */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Supply</label>
            <select value={stateOfSupply} onChange={(e) => setStateOfSupply(e.target.value)} data-testid="select-state"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* PO Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">PO Number</label>
            <input type="text" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Customer PO Number"
              data-testid="input-po-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* LUT/ARN Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">LUT/ARN Number</label>
            <input type="text" value={lutArnNumber} onChange={(e) => setLutArnNumber(e.target.value)} placeholder="LUT/ARN Number"
              data-testid="input-lut-arn" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Billing Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Billing Address</label>
            <input type="text" value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="Billing Address"
              data-testid="input-billing-address" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Payment Terms */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
            <select value={paymentTermId} onChange={(e) => setPaymentTermId(e.target.value)} data-testid="select-payment-term"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Payment Terms</option>
              {paymentTerms.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ========== LINE ITEMS SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
          <button onClick={addLineItem} data-testid="add-line-item-btn"
            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
            <Plus className="h-4 w-4" /> Add Row
          </button>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[1300px]">
            <div className="grid gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
              <div>#</div>
              <div>Product</div>
              <div>MRP</div>
              <div>Qty</div>
              <div></div>
              <div>Unit</div>
              <div>Price/Unit</div>
              <div>Disc %</div>
              <div>Disc Amt</div>
              <div>Tax</div>
              <div>Tax Amt</div>
              <div>Amount</div>
              <div>Warranty</div>
            </div>

            {lineItems.map((item, idx) => (
              <div key={item.key} data-testid={`line-item-${idx}`}
                className="grid gap-2 px-4 py-2 border-b border-slate-100 items-start"
                style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
                <div className="flex items-center gap-1 pt-2">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(idx)} data-testid={`remove-line-${idx}`}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div>
                  <select value={item.product_id} onChange={(e) => updateLineItem(idx, 'product_id', e.target.value)}
                    data-testid={`line-product-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">Select</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {item.product_id && item._stock_count !== null && (
                    <span className={`block text-xs mt-0.5 px-1 ${item._stock_count > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      Stock: {item._stock_count}
                    </span>
                  )}
                  <input type="text" value={item.serial_number} onChange={(e) => updateLineItem(idx, 'serial_number', e.target.value)}
                    placeholder="Serial Number" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  <input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  {item._is_part_sale && !item._is_full_sale && (
                    <input type="number" value={item.part_sale_amount} onChange={(e) => updateLineItem(idx, 'part_sale_amount', e.target.value)}
                      placeholder="Part Sale Amount" min="0" step="0.01" data-testid={`line-part-sale-${idx}`}
                      className="w-full px-2 py-1 border border-orange-200 bg-orange-50 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  )}
                </div>
                <div>
                  <input type="number" value={item.mrp} onChange={(e) => updateLineItem(idx, 'mrp', e.target.value)}
                    placeholder="0" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                  {item._is_part_sale && (
                    <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer">
                      <input type="checkbox" checked={item._is_full_sale} onChange={(e) => updateLineItem(idx, '_is_full_sale', e.target.checked)}
                        data-testid={`line-full-sale-${idx}`}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                      <span className="text-xs text-slate-600">Full Sale</span>
                    </label>
                  )}
                </div>
                <div>
                  <input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    min="0" data-testid={`line-qty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="pt-1">
                  {item.product_id && item._stock_count > 0 && (
                    <button type="button" onClick={() => openItemModal(idx)} data-testid={`line-items-btn-${idx}`}
                      className={`p-1.5 rounded transition-colors ${item._items_confirmed ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}
                      title={item._items_confirmed ? `${item._sold_item_ids.length} items selected` : 'Select items to sell'}>
                      {item._items_confirmed ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <div>
                  <select value={item.unit_id} onChange={(e) => updateLineItem(idx, 'unit_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <input type="number" value={item.price_per_unit} onChange={(e) => updateLineItem(idx, 'price_per_unit', e.target.value)}
                    placeholder="0" step="0.01" data-testid={`line-price-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <input type="number" value={item.discount_percentage} onChange={(e) => updateLineItem(idx, 'discount_percentage', e.target.value)}
                    min="0" max="100" step="0.01"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <span className="text-sm text-slate-600 px-2">{item.discount_amount?.toFixed(2)}</span>
                </div>
                <div>
                  <select value={item.tax_id} onChange={(e) => updateLineItem(idx, 'tax_id', e.target.value)}
                    data-testid={`line-tax-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                  </select>
                </div>
                <div>
                  <span className="text-sm text-slate-600 px-2">{item.tax_amount?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-900 px-2">{item.total_amount?.toFixed(2)}</span>
                </div>
                <div>
                  <input type="number" value={item.warranty_days} onChange={(e) => updateLineItem(idx, 'warranty_days', e.target.value)}
                    placeholder="Days" min="0" data-testid={`line-warranty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
            ))}

            <div className="grid gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm font-semibold"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
              <div></div>
              <div className="text-slate-700">TOTAL</div>
              <div></div>
              <div className="text-slate-700">{totalQty}</div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div className="text-slate-700">{totalDiscount.toFixed(2)}</div>
              <div></div>
              <div className="text-slate-700">{totalTax.toFixed(2)}</div>
              <div className="text-slate-900">{subtotal.toFixed(2)}</div>
              <div></div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== PAYMENT & SUMMARY SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left — Payment & Description */}
          <div className="space-y-4">
            {payments.map((p, idx) => (
              <div key={p.key} className="flex items-center gap-3">
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Payment Mode</label>
                    <select value={p.payment_type} onChange={(e) => updatePayment(idx, 'payment_type', e.target.value)}
                      data-testid={`payment-type-${idx}`}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                      <option value="">Select</option>
                      {paymentModes.map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                    </select>
                  </div>
                  {(p.payment_type === 'Online' || p.payment_type === 'UPI') && (() => {
                    const selectedCompany = companies.find(c => c.id === companyId);
                    if (!companyId || !selectedCompany) return (
                      <p className="text-xs text-amber-600">Please select a company first to see bank accounts</p>
                    );
                    const filterType = p.payment_type === 'Online' ? 'Net Banking' : 'UPI';
                    const filtered = (selectedCompany.bank_details || []).filter(bd => bd.type === filterType);
                    if (filtered.length === 0) return (
                      <p className="text-xs text-slate-400">No {filterType} accounts added for {selectedCompany.company_name}</p>
                    );
                    return (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {p.payment_type === 'Online' ? 'Bank Account' : 'UPI ID'}
                        </label>
                        <select value={p.bank_detail_id || ''} onChange={(e) => updatePayment(idx, 'bank_detail_id', e.target.value)}
                          data-testid={`bank-detail-${idx}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                          <option value="">-- Select --</option>
                          {filtered.map(bd => (
                            <option key={bd.id} value={bd.id}>
                              {bd.type === 'Net Banking' ? `${bd.bank_name || ''} - ${bd.account_no || ''}` : bd.upi_id || bd.bank_name || ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                {payments.length > 1 && (
                  <button onClick={() => removePayment(idx)} className="mt-5 p-2 text-slate-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Add Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Add a note or description..."
                data-testid="input-description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm resize-none" />
            </div>
          </div>

          {/* Right — Summary */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)}
                  data-testid="round-off-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                <span className="text-sm text-slate-600">Round Off</span>
                {roundOff && <span className="text-sm text-slate-500">{roundOffAmount >= 0 ? '+' : ''}{roundOffAmount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900 min-w-[100px] text-right" data-testid="total-amount">
                  ₹{total.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={isReceived} onChange={(e) => { setIsReceived(e.target.checked); if (e.target.checked) setReceivedAmount(String(total)); }}
                    data-testid="received-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                  <span className="text-sm text-slate-600">Received</span>
                </div>
                {isReceived && (
                  <input type="number" value={receivedAmount} onChange={(e) => setReceivedAmount(e.target.value)}
                    data-testid="received-amount" className="w-32 px-3 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">Balance</span>
                <span className={`text-lg font-bold min-w-[100px] text-right ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}
                  data-testid="balance-amount">
                  ₹{balance.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== ACTION BUTTONS ========== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between z-40">
        {!allItemsConfirmed && lineItems.some(li => li.product_id) && (
          <span className="flex items-center gap-1.5 text-amber-600 text-sm"><AlertTriangle className="h-4 w-4" /> Select item IDs for all products before saving</span>
        )}
        {(allItemsConfirmed || !lineItems.some(li => li.product_id)) && <span></span>}
        <button onClick={handleSave} disabled={submitting || !allItemsConfirmed} data-testid="save-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-2.5 rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>

      {/* ========== ITEM SELECTION MODAL ========== */}
      {itemModalIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Select Item IDs</h2>
              <button onClick={() => setItemModalIdx(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-1">
                Product: <span className="font-medium text-slate-900">{products.find(p => p.id === lineItems[itemModalIdx]?.product_id)?.name || '-'}</span>
              </p>
              <p className="text-sm text-slate-500 mb-4">
                Enter {itemModalIds.length} item ID(s) being sold. Available: {itemModalAvailable.length} in stock.
              </p>

              {itemModalErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4 text-sm">
                  {itemModalErrors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {itemModalIds.map((val, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
                    <input
                      type="text" value={val}
                      onChange={e => { const n = [...itemModalIds]; n[i] = e.target.value; setItemModalIds(n); setItemModalErrors([]); }}
                      list={`avail-items-${i}`}
                      placeholder="Enter or select item ID"
                      data-testid={`item-modal-input-${i}`}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-orange-500 focus:outline-none"
                    />
                    <datalist id={`avail-items-${i}`}>
                      {itemModalAvailable.map(a => <option key={a.item_id} value={a.item_id} />)}
                    </datalist>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button onClick={() => setItemModalIdx(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={validateAndConfirmItems} disabled={itemModalValidating || itemModalIds.some(v => !v.trim())}
                  data-testid="item-modal-confirm"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {itemModalValidating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Validate & Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
